import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useEditor, EditorContent } from '@tiptap/react';
import { clsx } from 'clsx';
import { memo, useRef, useState, useMemo, useCallback, useEffect } from 'react';
import FixedToolbar from './Components/FixedToolbar.js';
import InlineToolbar from './Components/InlineToolbar.js';
import BubbleImageOptions from './Extensions/BubbleImageOptions.js';
import Resizer from './Extensions/Resizer.js';
import { getTiptapDataValue } from './content.js';
import { styled, css } from 'styled-components';
import * as Y from 'yjs';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';
import { mediaMobile, mediaHover } from '@fabric-app/ui/constants/styled-utils';
import { restoreDataTransfer } from '@fabric-app/utils/clipboard';
import { useRunOnce } from '@fabric-app/hooks/useRunOnce';
import { FloatingEmbedResourceToolbar } from './Extensions/EmbedResource/FloatingEmbedResourceToolbar.js';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { throttle } from 'lodash-es';
import { isObject, isDefined } from '@fabric-app/utils/guards';
import { useIsFeatureFlagEnabled, FeatureFlag } from '../feature-flags/feature-flags.store.js';
import { useUnmount } from '@fabric-app/hooks/useUnmount';
import { useRegisterSubDocumentSearch } from '../search/context/SubDocumentSearchContext.js';
import { ScrollArea } from '@fabric-app/ui/components/ScrollArea';
import { ContextualMenu } from './Extensions/ContextualMenu/Components/ContextualMenuComponent.js';
import { getTiptapExtensions } from './Extensions/index.js';
import { AbsolutePosition } from '@fabric-app/ui/components/AbsolutePosition';
import { Spinner } from '@fabric-app/ui/components/Spinner';
import { TiptapTitle } from './TiptapTitle.js';
import { animateAnnotationFocus } from '../annotation/constants/animations.js';

const TiptapScrollarea = styled(ScrollArea.Default).attrs((p) => {
  return {
    ...p,
    ["data-tiptap-scrollarea"]: true
  };
}).withConfig({
  displayName: "Tiptap__TiptapScrollarea",
  componentId: "sc-8ecdb5f4-0"
})([
  `> [data-radix-scroll-area-viewport] > div{position:relative;overscroll-behavior:contain !important;-webkit-overflow-scrolling:touch !important;}`
]);
const TiptapStyles = css([
  `--node-control-offset-y:1px;:not(.ProseMirror-widget) > *:not(.ProseMirror-widget){--node-control-offset-y:1px;--node-control-offset-x:0px;}.ProseMirror-gapcursor:after{border-top-color:`,
  `;}width:100%;flex-grow:1;margin:0 auto;min-height:100%;max-width:1082px;z-index:1;.ProseMirror-draghandle-selected{opacity:0.5;}.collaboration-cursor{display:inline;border-left:1px solid #0d0d0d;border-right:1px solid #0d0d0d;margin-left:-1px;margin-right:-1px;width:0px;position:relative;word-break:normal;border-color:var(--yjs-cursor-color);transition:opacity 0.2s ease-in-out;pointer-events:none;user-select:none;-moz-user-select:-moz-none;&::before{content:attr(data-yjs-cursor-name);font-family:'Inter',sans-serif;font-style:normal;font-weight:600;left:-1px;line-height:normal;position:absolute;user-select:none;white-space:nowrap;font-size:14px;color:#fff;top:-1.4em;border-radius:6px;border-bottom-left-radius:0;padding:2px 6px;pointer-events:none;user-select:none;-moz-user-select:-moz-none;z-index:1;background-color:var(--yjs-cursor-color);}}.is_empty:not(:has(.is_empty))::before{content:attr(data-placeholder);color:rgba(var(--color-foreground),0.3);pointer-events:none;user-select:none;float:left;white-space:nowrap;text-overflow:ellipsis;width:100%;overflow:hidden;height:auto;position:absolute;color:`,
  `;}.fbc-document-title-empty{&:before{content:'Untitled note';color:rgba(var(--color-foreground),0.3);pointer-events:none;user-select:none;float:left;white-space:nowrap;text-overflow:ellipsis;width:100%;overflow:hidden;height:auto;position:absolute;color:`,
  `;}}&:focus{outline:none;}.tableWrapper{padding:1rem 0;overflow-x:auto;min-width:0;}.resize-cursor{cursor:ew-resize;cursor:col-resize;}[data-diff-preview-deletion='true']{background:rgba(255,0,30,0.2);mix-blend-mode:multiply;}[data-diff-preview-addition='true']{background:rgba(0,255,13,0.3);mix-blend-mode:multiply;}mark{[data-diff-preview-deletion='true']{background:rgba(255,120,60,0.25);}[data-diff-preview-addition='true']{background:rgba(170,255,70,0.35);}}pre{[contenteditable='false']{white-space:pre-wrap;}}[data-diff-preview-from='true'],.fbc-list:has(.fbc-list-content:has([data-diff-preview-from='true']:first-child)){margin-top:24px;}.fbc-list-content > [data-diff-preview-from='true']:first-child,.fbc-list-content > [data-node-view-content] > [data-node-view-content-react] > [data-diff-preview-from='true']:first-child{margin-top:0;}`,
  `{[data-diff-preview-from='true']{margin-top:48px;}}`
], cssVar["color-text-primary"], cssVar["color-text-placeholder"], cssVar["color-text-placeholder"], mediaMobile);
const TiptapNodeStyles = css([
  `color:`,
  `;--node-gap:0.188rem;line-height:1.5;p{padding:var(--node-gap) 0;white-space:break-spaces;word-break:break-word;}ul,ol{padding:0 1rem;}h1{--node-control-offset-y:6px;}h1,h2,h3,h4,h5,h6{display:block;font-size:1.875em;line-height:1.3;font-weight:600;margin-block-start:0em;margin-block-end:0.3em;margin-inline-start:0px;margin-inline-end:0px;&:not(:first-child){margin-top:1em;}}h2{--node-control-offset-y:5px;font-size:1.5em;}h3{--node-control-offset-y:2px;font-size:1.25em;}h4{font-size:1.15em;}h5{font-size:1.13em;}h6{font-size:1.11em;}code{background:rgba(`,
  `,0.1);padding:0.2em 0.3em;border-radius:0.2em;font-family:'Courier Prime',monospace;color:rgba(`,
  `,0.95);& > *{font-family:'Courier Prime',monospace;}}mark{background:rgba(251,243,219,1);}[data-annotation='true']{background-color:rgba(var(--annotation-color,`,
  `),0.2);transition:background-color 0.15s ease;will-change:background-color;`,
  `{&:hover{background-color:rgba(var(--annotation-color,`,
  `),0.4);}&:has([data-annotation-hovered='true']){background-color:rgba(var(--annotation-color,`,
  `),0.4);}}&[data-focus='true'],&:has([data-focus='true']){`,
  `}}img,[data-img-failure]{max-width:100%;height:auto;.ProseMirror-selectednode{outline:2px solid rgba(`,
  `,0.3);}}[data-img-failure]{background:`,
  `;}.node-image.ProseMirror-selectednode{outline:2px solid rgba(`,
  `,0.3);}[data-search-match]{background-color:#fffc9e71;}[data-search-match-active]{background-color:#ffdd0071;}blockquote{--node-control-offset-x:18px;--node-control-offset-y:-4px;padding:var(--node-gap) 0;padding-left:1rem;margin-top:4px;margin-bottom:4px;border-left:2px solid `,
  `;}hr{border:none;border-top:2px solid `,
  `;margin:2rem 0;&.ProseMirror-selectednode{outline:2px solid rgba(`,
  `,0.3);}}a{color:`,
  `;text-decoration:underline;}ul,ol{margin:0;margin-top:0.5rem;padding-left:1em;outline:none;list-style:disc;ul,ol{padding-left:1.4em;}ol ol{padding-left:1.4em;}ul ul{padding-left:1.4em;}}li{margin-top:0.25rem;}ol{list-style-type:none;counter-reset:item;padding-left:0;}ol li{counter-increment:item;display:grid;grid-template-columns:auto 1fr;align-items:start;}ol li::before{content:counters(item,'.') '. ';grid-column:1;grid-row:1 / span 999;}ol ol{list-style-type:none;}ol ol li::before{content:counters(item,'.') '. ';}pre{background:rgba(`,
  `,0.04);border-radius:0.5rem;font-family:'Courier Prime',monospace;padding:0.75rem 1rem;margin-top:4px;margin-bottom:4px;code{--node-control-offset-x:16px;--node-control-offset-y:-12px;color:inherit;font-size:1rem;padding:0;background:none;}}table{border-collapse:collapse;margin:0;overflow:hidden;table-layout:fixed;width:100%;td,th{border:2px solid `,
  `;box-sizing:border-box;min-width:1em;padding:3px 5px;position:relative;vertical-align:top;> *{margin-bottom:0;}}th{background-color:`,
  `;font-weight:bold;text-align:left;}.selectedCell:after{background:rgba(`,
  `,0.06);content:'';left:0;right:0;top:0;bottom:0;pointer-events:none;position:absolute;z-index:2;}.column-resize-handle{background-color:rgba(`,
  `,0.3);bottom:-2px;position:absolute;right:-2px;cursor:col-resize;top:0;width:4px;}.column-resize-dragging .column-resize-handle{&::after{content:'';position:absolute;top:-2vh;left:-2vw;right:-2vw;bottom:-2vh;z-index:125;cursor:col-resize;}}p{margin:0;}}[data-role='callout']{--node-control-offset-x:11px;--node-control-offset-y:-12px;margin-top:4px;margin-bottom:4px;display:flex;flex-direction:column;padding:5px;border-radius:5px;padding:12px;background:`,
  `;> h1,h2,h3,h4,h5,h6{margin:0;}}.fbc-node-control{position:relative;display:inline;display:contents;`,
  `{display:none;visibility:hidden;pointer-events:none;}}.fbc-list{display:flex;align-items:flex-start;width:100%;padding-left:2px;position:relative;&[data-type='fbc-toggle-list'][data-empty-content='true']{& > .fbc-list-main > .fbc-list-content{& > [data-diff-display='true']:last-child{margin-bottom:0 !important;}}&:has(> .fbc-list-main > .fbc-list-content > [data-diff-display='true']:last-child){margin-bottom:38px;}}&:has(.fbc-list-main){flex-wrap:wrap;}.fbc-list-main{display:flex;align-items:flex-start;width:100%;}&[data-type='fbc-check-list'][data-checked='true'] > .fbc-list-content > p{text-decoration:line-through;}.fbc-list-prefix{flex-shrink:0;display:flex;align-items:center;justify-content:center;width:unset;user-select:none;margin-right:2px;width:24px;padding:var(--node-gap) 0;box-sizing:content-box;.fbc-list-prefix-content{&::before{content:var(--fbc-list-prefix);font-size:var(--fbc-list-prefix-font-size);line-height:var(--fbc-list-prefix-line-height);font-family:var(--fbc-list-prefix-font-family);}.fbc-list-chevron{transition:color 0.1s ease-in-out;}.fbc-list-chevron > svg{transition:transform 0.1s ease-in-out;transform:rotate(0deg);}&[data-expanded='false'] > .fbc-list-chevron > svg{transform:rotate(-90deg);}&[data-empty-content='true'] > .fbc-list-chevron{color:`,
  `;}}}.fbc-list-content{--node-control-offset-x:24px;--node-control-offset-y:3px;display:flex;flex-direction:column;flex-grow:1;position:relative;}&[data-expanded='false'] > .fbc-list-main > .fbc-list-content{> :not(.ProseMirror-widget) ~ :not(.ProseMirror-widget){display:none;}}.fbc-list-empty-content{display:none;}&[data-empty-content='true'][data-expanded='true']{> .fbc-list-empty-content{display:block;user-select:none;z-index:0;&::after{white-space:nowrap;overflow:hidden;text-overflow:ellipsis;z-index:1;content:'Empty toggle. Click or drop content inside.';word-break:break-word;white-space:pre-wrap;color:`,
  `;`,
  `{content:'Empty toggle. Tap to add content.';}}&::before{content:'';background:transparent;left:24px;top:-2px;bottom:-2px;right:-2px;position:absolute;z-index:-1;border-radius:0.25em;}position:relative;cursor:pointer;padding:var(--node-gap) 0 var(--node-gap) 26px;flex-basis:100%;transition:background 0.2s ease-in-out;`,
  `{&:hover{&::before{background:rgba(`,
  `,0.04);}}}&[data-dropping='true']{&::before{background:rgba(`,
  `,0.04);}}}}}[data-block='true']{display:flex;flex-direction:column;> :not(.ProseMirror-widget) ~ :not(.ProseMirror-widget){margin-left:calc(24px + 3px);}}.contextual-menu{background:rgba(`,
  `,0.08);border-radius:0.375rem;outline:0.25rem solid rgba(`,
  `,0.08);color:`,
  `;text-decoration:none;&[data-contextual-menu-placeholder]{&::after{content:attr(data-contextual-menu-placeholder);color:`,
  `;}}}.contextual-menu-selected{box-shadow:0 0 0 0.25rem rgba(`,
  `,0.05),inset 0 0 0 100rem rgba(`,
  `,0.05);border-radius:0.375rem;th{background:rgba(`,
  `,0.02);}}.contextual-menu-node{position:relative;&::before{content:attr(data-contextual-menu-placeholder,' ');color:`,
  `;background:`,
  `;border-radius:0.375rem;outline:0.25rem solid `,
  `;z-index:-1;position:absolute;}}[data-deprecated='true']{display:none;}`
], cssVar["color-text-secondary"], cssVar["color-bg-primary-reverse-rgb"], cssVar["color-bg-primary-reverse-rgb"], cssVar["color-bg-annotation-rgb"], mediaHover, cssVar["color-bg-annotation-rgb"], cssVar["color-bg-annotation-rgb"], animateAnnotationFocus, cssVar["color-app-primary"], cssVar["color-bg-secondary"], cssVar["color-app-primary"], cssVar["color-border-primary"], cssVar["color-border-primary"], cssVar["color-app-primary-rgb"], cssVar["color-app-primary-text"], cssVar["color-bg-primary-reverse-rgb"], cssVar["color-border-secondary"], cssVar["color-bg-tertiary"], cssVar["color-app-primary-rgb"], cssVar["color-app-primary-text-rgb"], cssVar["color-bg-tertiary"], mediaMobile, cssVar["color-text-tertiary"], cssVar["color-text-placeholder"], mediaMobile, mediaHover, cssVar["color-bg-contrast-reverse-rgb"], cssVar["color-bg-contrast-reverse-rgb"], cssVar["color-bg-contrast-reverse-rgb"], cssVar["color-bg-contrast-reverse-rgb"], cssVar["color-text-primary"], cssVar["color-text-tertiary"], cssVar["color-app-primary-rgb"], cssVar["color-app-primary-rgb"], cssVar["color-app-primary-rgb"], cssVar["color-text-tertiary"], cssVar["color-bg-tertiary"], cssVar["color-bg-tertiary"]);
const getEditorAttributes = ({ editable, tabIndex, contentClassName }) => {
  return {
    class: clsx("fabric-tiptap", contentClassName),
    ...!editable ? {
      tabindex: "-1"
    } : {
      ...tabIndex && {
        tabindex: tabIndex
      }
    }
  };
};
const EditorContainerStyled = styled.div.withConfig({
  displayName: "Tiptap__EditorContainerStyled",
  componentId: "sc-8ecdb5f4-1"
})([
  `display:flex;flex-direction:column;overflow:hidden;flex-grow:1;position:relative;height:100%;max-height:100%;flex-shrink:0;max-width:100%;color:`,
  `;--tiptap-padding-vertical:3.125rem;--tiptap-padding-horizontal:4.125rem;cursor:text;> div:not([role='toolbar']):not([data-tippy-root]):not([data-toolbar-wrapper]):not( .moveable-control-box ):not([data-tiptap-bottom]):not([data-drag-handle]){height:1px;flex-grow:1;overflow:visible;}&[data-disable-scrolling='true']{height:auto;> div:not([role='toolbar']):not([data-tippy-root]):not([data-toolbar-wrapper]){overflow-y:hidden;}}[data-tiptap-bottom]{order:3;}`,
  `{[data-tiptap-bottom]{order:0;}}.fabric-tiptap{`,
  ` `,
  ` padding:var(--tiptap-padding-vertical) var(--tiptap-padding-horizontal);padding-top:0;& > [data-role='callout']:first-child:not([data-compat]){display:none;}}[data-tiptap-editor-content='true']{min-height:100%;flex-shrink:0;height:max-content;}`
], cssVar["color-text-secondary"], mediaMobile, TiptapStyles, TiptapNodeStyles);
const EditorContainer = ({ tiptapProps, className }) => {
  const { pastedContent, initialValue, className: propClassName, contentClassName, editable = true, disableScrolling = false, style, onChange, onInitialize, editorRef, onEditorFocusChange, onUpload, onCheckBeforeUpload, onDeleteComment, onCreateEmptyComment, autoFocus, tabIndex, yDoc: yDocProp, onlineProvider: provider, userAwareness, onToolbarHeightChange, bottomElement, onCreate, title, onTitleChange, isLoading, suppressMissingUploadError } = tiptapProps;
  const containerRef = useRef(null);
  const [contentRef, setContentRef] = useState(null);
  const { isMobileView } = useResponsive();
  const initializedRef = useRef(false);
  const contextualMenuRef = useRef(null);
  const yDoc = useMemo(() => yDocProp ?? new Y.Doc(), [
    yDocProp
  ]);
  const userAwarenessRef = useRef(userAwareness);
  userAwarenessRef.current = userAwareness;
  const extensions = useMemo(() => getTiptapExtensions({
    yDoc,
    provider,
    userAwareness: userAwarenessRef.current ?? void 0,
    contextualMenu: {
      onMenuActive: (query, view, element, spec, mobile) => {
        contextualMenuRef.current?.onMenuActive(query, view, element, spec, mobile);
      },
      onMenuInactive: (view) => {
        contextualMenuRef.current?.onMenuInactive(view);
      },
      onMenuUpdated: (query, view, element, spec, mobile) => {
        contextualMenuRef.current?.onMenuUpdated(query, view, element, spec, mobile);
      },
      handleDOMEvents: {
        keydown: (_query, _view, _spec, _event) => {
          return contextualMenuRef.current?.onKeyDown(_event);
        }
      }
    },
    virtualCursorTarget: () => containerRef.current ?? document.body,
    suppressMissingUploadError,
    onDeleteComment,
    onCreateEmptyComment
  }), [
    yDoc,
    provider,
    suppressMissingUploadError,
    onDeleteComment,
    onCreateEmptyComment
  ]);
  const blurTimeout = useRef(void 0);
  const onUpdate = useCallback(({ editor: editor2, transaction }) => {
    if (!transaction.docChanged) return;
    const content = getTiptapDataValue(editor2);
    onChange?.(content, yDoc, transaction, transaction.docChanged);
  }, [
    onChange,
    yDoc
  ]);
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;
  const throttledOnUpdate = useMemo(() => throttle(onUpdateRef.current, 300, {
    leading: false,
    trailing: true
  }), []);
  const useProsemirrorDevTools = useIsFeatureFlagEnabled(FeatureFlag.PROSEMIRROR_DEV_TOOLS);
  let unmountDevTools;
  const [isEditorFocused, setIsEditorFocused] = useState(false);
  const editor = useEditor({
    content: initialValue?.content,
    extensions,
    autofocus: autoFocus ? true : false,
    editable,
    immediatelyRender: false,
    shouldRerenderOnTransaction: false,
    onUpdate: throttledOnUpdate,
    onTransaction: ({ editor: editor2 }) => {
      if (initializedRef.current) return;
      initializedRef.current = true;
      onInitialize?.(getTiptapDataValue(editor2));
    },
    editorProps: {
      attributes: getEditorAttributes({
        editable,
        tabIndex,
        contentClassName
      })
    },
    onFocus: () => {
      window.clearTimeout(blurTimeout.current);
      onEditorFocusChange?.(true);
      setIsEditorFocused(true);
    },
    onBlur: () => {
      blurTimeout.current = window.setTimeout(() => {
        if (document.activeElement?.dataset?.role === "expanded-fdoc-title-input") return;
        const activeElement = document.activeElement;
        if (activeElement?.dataset?.tiptapToolbar || activeElement?.closest("[data-tiptap-toolbar]")) {
          return;
        }
        onEditorFocusChange?.(false);
        setIsEditorFocused(false);
      }, 10);
    },
    onCreate: ({ editor: editor2 }) => {
      setTimeout(() => {
        if ((editor2.isEmpty || editor2.getText().trim().length === 0) && autoFocus) {
          editor2.commands.focus();
        }
        if (containerRef.current) {
          const editorElement = containerRef.current.querySelector(".ProseMirror");
          if (!editorElement) return;
          setContentRef(editorElement);
        }
      }, 100);
      editor2.commands.setUploadHandler(onUpload);
      editor2.commands.setCheckBeforeUploadHandler(onCheckBeforeUpload);
      if (onDeleteComment) editor2.commands.setHandleDeleteComment(onDeleteComment);
      if (onCreateEmptyComment) editor2.commands.setHandleCreateEmptyComment(onCreateEmptyComment);
      if (useProsemirrorDevTools) {
        import('prosemirror-dev-tools').then(({ default: applyDevTools }) => {
          unmountDevTools = applyDevTools(editor2.view);
        });
      }
      onCreate?.(editor2);
    }
  });
  const { onSearchStateChange } = useRegisterSubDocumentSearch({
    onSearch: (regex) => {
      editor?.commands.setSearch(regex);
      return Promise.resolve(void 0);
    },
    onGoToMatch: (match) => {
      editor?.commands.goToMatch(match);
    }
  });
  useEffect(() => {
    if (!editor) return;
    editor.commands.setOnSearchStateChange((state) => {
      onSearchStateChange({
        currentIndex: state.currentIndex,
        totalMatches: state.totalMatches
      });
    });
    return () => {
      editor.commands.setOnSearchStateChange(void 0);
    };
  }, [
    onSearchStateChange,
    editor
  ]);
  useUnmount(() => {
    unmountDevTools?.();
  }, {
    avoidStrictMode: true
  });
  useEffect(() => {
    if (!editor) return;
    editor.setEditable(editable);
    editor.setOptions({
      editorProps: {
        attributes: getEditorAttributes({
          editable,
          tabIndex,
          contentClassName
        })
      }
    });
  }, [
    editable,
    editor,
    contentClassName,
    tabIndex
  ]);
  useEffect(() => {
    if (!editor || !userAwareness || !provider) return;
    editor.commands.updateUser(userAwareness);
  }, [
    editor,
    userAwareness,
    provider
  ]);
  useEffect(() => {
    if (!editor) return;
    if (typeof editorRef === "object") {
      editorRef.current = editor;
    } else if (typeof editorRef === "function") {
      editorRef(editor);
    }
  }, [
    editor,
    editorRef
  ]);
  const onClickContainer = (e) => {
    if (e.target !== e.currentTarget || !editor) return;
    editor.commands.focus();
    e.preventDefault();
  };
  useRunOnce(() => {
    if (!editor || !pastedContent) return;
    let dataTransfer;
    if (typeof pastedContent === "string") {
      dataTransfer = new DataTransfer();
      dataTransfer.setData("text/plain", pastedContent);
    } else dataTransfer = restoreDataTransfer(pastedContent);
    const dom = editor.view.dom;
    const event = new ClipboardEvent("paste", {
      clipboardData: dataTransfer,
      composed: true
    });
    dom.dispatchEvent(event);
  }, {
    condition: Boolean(editor && pastedContent)
  });
  const showExtraToolbars = !!editable && !!editor;
  return /* @__PURE__ */ jsxs(EditorContainerStyled, {
    "data-tiptap-container": true,
    "data-disable-scrolling": disableScrolling,
    className: clsx(propClassName, className),
    style,
    ref: containerRef,
    onClick: onClickContainer,
    "data-tiptap-editor-container": true,
    onTouchMove: (e) => {
      if (!editor?.isFocused) return;
      e.stopPropagation();
    },
    children: [
      isLoading && /* @__PURE__ */ jsx(AbsolutePosition, {
        top: 22,
        right: 22,
        children: /* @__PURE__ */ jsx(Spinner, {
          size: 20,
          thickness: 5
        })
      }),
      /* @__PURE__ */ jsxs(TiptapScrollarea, {
        scrollbarVariant: "subtle",
        asFlex: true,
        innerDisplayFlex: true,
        expandVertically: true,
        innerHeightMaxContent: true,
        children: [
          editor && /* @__PURE__ */ jsx(ContextualMenu, {
            ref: contextualMenuRef,
            editor,
            // Null assertion because this is literally inside the container ref
            mobileTarget: containerRef.current
          }),
          isDefined(title) && onTitleChange && editor && /* @__PURE__ */ jsx(TiptapTitle, {
            readOnly: !editable,
            title,
            onTitleChange,
            editor
          }),
          /* @__PURE__ */ jsx(EditorContent, {
            style: {
              zIndex: "1",
              flexGrow: 1,
              // allows child elements to infer % height, e.g. min-height
              flexBasis: 1
            },
            "data-pan-id-ignore-resource-drag-resize": "xy",
            editor,
            "data-or-obscured": true,
            "data-tiptap-content-scrollbar": true,
            "data-tiptap-editor-content": true
          }, "tiptap-editor-content"),
          showExtraToolbars && /* @__PURE__ */ jsxs(Fragment, {
            children: [
              /* @__PURE__ */ jsx(BubbleImageOptions, {
                editor
              }, "tiptap-image-options"),
              /* @__PURE__ */ jsx(FloatingEmbedResourceToolbar, {
                editor
              }, "tiptap-embed-toolbar"),
              contentRef && !isMobileView && /* @__PURE__ */ jsx(InlineToolbar, {
                editor,
                contentRef
              }, "tiptap-inline-toolbar"),
              /* @__PURE__ */ jsx(Resizer, {
                editor
              })
            ]
          })
        ]
      }),
      /* @__PURE__ */ jsx("div", {
        "data-tiptap-bottom": true,
        children: bottomElement
      }),
      editable && editor && isMobileView && /* @__PURE__ */ jsx(FixedToolbar, {
        mobileTarget: containerRef.current,
        onToolbarHeightChange,
        editor,
        isEditorFocused
      }, "tiptap-fixed-toolbar")
    ]
  });
};
const Tiptap = (props) => {
  return /* @__PURE__ */ jsx(EditorContainer, {
    tiptapProps: props
  });
};
const Tiptap$1 = /* @__PURE__ */ memo(Tiptap, (prevProps, nextProps) => {
  const propsToCompare = [
    "className",
    "contentClassName",
    "editable",
    "disableScrolling",
    "allowMultiplayer",
    "autoFocus",
    "tabIndex"
  ];
  for (const prop of propsToCompare) {
    if (prevProps[prop] !== nextProps[prop]) {
      return false;
    }
  }
  if (!shallowEqual(prevProps.style, nextProps.style) || !shallowEqual(prevProps.contentStyle, nextProps.contentStyle)) {
    return false;
  }
  if (prevProps.onChange !== nextProps.onChange || prevProps.onInitialize !== nextProps.onInitialize || prevProps.onEditorFocusChange !== nextProps.onEditorFocusChange || prevProps.onUpload !== nextProps.onUpload || prevProps.onToolbarHeightChange !== nextProps.onToolbarHeightChange) {
    return false;
  }
  if (prevProps.editorRef !== nextProps.editorRef || prevProps.yDoc !== nextProps.yDoc || prevProps.onlineProvider !== nextProps.onlineProvider || prevProps.userAwareness !== nextProps.userAwareness || prevProps.bottomElement !== nextProps.bottomElement) {
    return false;
  }
  if (prevProps.userAwareness && nextProps.userAwareness) {
    if (prevProps.userAwareness.name !== nextProps.userAwareness.name || prevProps.userAwareness.color !== nextProps.userAwareness.color) {
      return false;
    }
  } else if (prevProps.userAwareness !== nextProps.userAwareness) {
    return false;
  }
  return true;
});
function shallowEqual(obj1, obj2) {
  if (obj1 === obj2) return true;
  if (!obj1 || !obj2) return false;
  if (!isObject(obj1) || !isObject(obj2)) return false;
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  if (keys1.length !== keys2.length) return false;
  for (const key of keys1) {
    if (!(key in obj2) || !(key in obj1)) return false;
    if (obj1[key] !== obj2[key]) return false;
  }
  return true;
}

export { TiptapNodeStyles, TiptapStyles, Tiptap$1 as default };
//# sourceMappingURL=Tiptap.js.map
